import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { z } from "zod";
import type { TokenResponse } from "../../domain/types/auth";
import {
  PENDING_INVITATION_TOKEN_KEY,
  type InvitationLoginMethod,
  type InvitationPreview,
} from "../../domain/types/staff";
import type { SocialLoginType } from "../../shared/types/common";
import { invitationApi } from "../../infrastructure/api/company-staff-api-service";
import { resolveErrorMessage } from "../../shared/utils/save-flow";
import { useAuthStore } from "../stores/auth-store";
import { useSocialLogin } from "../hooks/use-social-login";
import { Input } from "../components/UI/Input";
import { Button } from "../components/UI/Button";

// 회원가입 규칙은 EmailSignupPage 와 같다
const PasswordSchema = z
  .string()
  .min(8, "비밀번호는 8자 이상이어야 합니다")
  .max(16, "비밀번호는 16자 이하여야 합니다")
  .regex(/[a-zA-Z]/, "영문자를 포함해야 합니다")
  .regex(/[0-9]/, "숫자를 포함해야 합니다")
  .regex(/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/, "특수문자를 포함해야 합니다")
  .regex(/^[a-zA-Z0-9!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]+$/, "영문, 숫자, 특수문자만 입력 가능합니다");

const SignupFormSchema = z
  .object({
    name: z.string().trim().min(1, "이름을 입력해주세요"),
    password: PasswordSchema,
    confirmPassword: z.string().min(1, "비밀번호를 다시 입력해 주세요"),
    agreementTerms: z.boolean().refine((v) => v, { message: "서비스 이용약관에 동의해주세요" }),
    agreementPrivacy: z.boolean().refine((v) => v, { message: "개인정보 수집 및 이용에 동의해주세요" }),
    agreementAge: z.boolean().refine((v) => v, { message: "만 14세 이상 확인에 동의해주세요" }),
    agreementMarketing: z.boolean(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "비밀번호가 일치하지 않습니다",
    path: ["confirmPassword"],
  });

type SignupForm = z.infer<typeof SignupFormSchema>;

const LoginFormSchema = z.object({ password: z.string().min(1, "비밀번호를 입력해주세요") });
type LoginForm = z.infer<typeof LoginFormSchema>;

const SOCIAL_PROVIDERS: { method: InvitationLoginMethod; provider: SocialLoginType; icon: string; label: string }[] = [
  { method: "NAVER", provider: "naver", icon: "/assets/icons/naver-login.svg", label: "네이버로 로그인" },
  { method: "KAKAO", provider: "kakao", icon: "/assets/icons/kakao-login.svg", label: "카카오로 로그인" },
  { method: "GOOGLE", provider: "google", icon: "/assets/icons/google-login.svg", label: "구글로 로그인" },
];

const formatDate = (value: string) =>
  new Date(value).toLocaleString("ko-KR", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });

/**
 * 회사 관리자 초대 수락 (/auth/invitation?token=).
 *
 * 초대받은 이메일의 계정 상태에 따라 갈린다.
 * - 계정 있음(이 회사 / 다른 회사): 기존 계정으로 로그인하면 수락된다. 소셜 계정이면 소셜 로그인으로.
 * - 계정 없음: 이메일이 고정된 회원가입 폼으로 가입과 수락을 한 번에 한다.
 * 수락이 끝나면 받은 토큰으로 로그인까지 마치고 그 회사 MES 로 들어간다.
 */
export const InvitationAcceptPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const completeLogin = useAuthStore((state) => state.completeLogin);

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  // 소셜 수락이 실패하면 AuthSuccessPage 가 사유를 state 로 넘겨 준다
  const [error, setError] = useState<string | null>(
    (location.state as { error?: string } | null)?.error ?? null,
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setLoadError("유효하지 않은 초대 링크입니다.");
      return;
    }
    (async () => {
      try {
        setPreview(await invitationApi.preview(token));
      } catch (e) {
        setLoadError(resolveErrorMessage(e, "유효하지 않은 초대 링크입니다."));
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const finish = async (accept: () => Promise<TokenResponse>) => {
    setSubmitting(true);
    setError(null);
    try {
      const tokenResponse = await accept();
      await completeLogin(tokenResponse);
      const companyId = tokenResponse.companyId ?? preview?.companyId;
      navigate(companyId ? `/company/${companyId}` : "/");
    } catch (e) {
      setError(resolveErrorMessage(e, "초대를 수락하지 못했습니다."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4285F4] mx-auto mb-4"></div>
          <p className="text-[#555]">초대를 확인하고 있습니다...</p>
        </div>
      </div>
    );
  }

  if (loadError || !preview) {
    return <StatusCard title="초대 링크 오류" message={loadError ?? "유효하지 않은 초대 링크입니다."} />;
  }

  const expired = preview.status === "EXPIRED" || (preview.status === "PENDING" && new Date(preview.expiresAt) < new Date());
  if (expired) {
    return (
      <StatusCard
        title="만료된 초대입니다"
        message={`초대 유효기간(${formatDate(preview.expiresAt)})이 지났습니다. ${preview.companyName} 담당자에게 초대를 다시 보내 달라고 요청해 주세요.`}
      />
    );
  }
  if (preview.status === "REVOKED") {
    return <StatusCard title="취소된 초대입니다" message={`${preview.companyName}에서 이 초대를 취소했습니다.`} />;
  }
  if (preview.status === "ACCEPTED") {
    return (
      <StatusCard
        tone="blue"
        title="이미 수락한 초대입니다"
        message="초대받은 계정으로 로그인하면 회사 관리 화면을 이용할 수 있습니다."
      />
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-10">
      <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-[#222] mb-2">회사 관리자 초대</h1>
          <p className="text-[#555]">
            <span className="font-medium text-[#222]">{preview.companyName}</span>에서
            {preview.inviterName ? ` ${preview.inviterName}님이` : ""} 회사 관리자로 초대했습니다.
          </p>
          <p className="mt-2 inline-block px-2 py-0.5 rounded-[4px] text-[12px] font-medium bg-[#E8EFFF] text-[#4285F4]">
            {preview.email}
          </p>
        </div>

        {error && (
          <div className="bg-[#EBF3FF] border border-[#4285F4]/20 rounded-[8px] p-3 mb-4">
            <p className="text-sm text-[#4285F4]">{error}</p>
          </div>
        )}

        {preview.accountState === "NEW" ? (
          <SignupSection
            email={preview.email}
            submitting={submitting}
            onSubmit={(form) =>
              finish(() =>
                invitationApi.acceptWithSignup(token, {
                  name: form.name.trim(),
                  password: form.password,
                  termsOfServiceAgreed: form.agreementTerms,
                  privacyPolicyAgreed: form.agreementPrivacy,
                  over14YearsOld: form.agreementAge,
                  promotionNewsSubscribed: form.agreementMarketing,
                }),
              )
            }
          />
        ) : (
          <LoginSection
            token={token}
            preview={preview}
            submitting={submitting}
            onSubmit={(password) => finish(() => invitationApi.acceptWithPassword(token, password))}
          />
        )}
      </div>
    </div>
  );
};

const LoginSection: React.FC<{
  token: string;
  preview: InvitationPreview;
  submitting: boolean;
  onSubmit: (password: string) => void;
}> = ({ token, preview, submitting, onSubmit }) => {
  const { login: socialLogin } = useSocialLogin();
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({ resolver: zodResolver(LoginFormSchema) });

  // 소셜 전용 계정이면 비밀번호가 없다. 로그인 수단을 모르면(빈 목록) 비밀번호 칸을 보여 준다
  const hasEmailLogin = preview.loginMethods.length === 0 || preview.loginMethods.includes("EMAIL");
  const socials = SOCIAL_PROVIDERS.filter((s) => preview.loginMethods.includes(s.method));

  const startSocial = (provider: SocialLoginType) => {
    // 콜백(AuthSuccessPage)에서 이 값을 보고 초대 수락으로 이어 간다
    sessionStorage.setItem(PENDING_INVITATION_TOKEN_KEY, token);
    socialLogin(provider);
  };

  return (
    <div>
      {preview.accountState === "ELSEWHERE" && (
        <p className="text-[13px] text-[#555] bg-[#FAFAFA] rounded-[8px] px-3 py-2 mb-4">
          이미 POD24 계정이 있습니다. 기존 계정으로 로그인하면 이 회사가 워크스페이스로 추가됩니다.
        </p>
      )}

      {hasEmailLogin && (
        <form onSubmit={handleSubmit((data) => onSubmit(data.password))} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#555] mb-1">이메일</label>
            <Input value={preview.email} readOnly disabled style={{ width: "100%", height: "48px", borderRadius: "8px" }} />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#555] mb-1">비밀번호</label>
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="비밀번호를 입력해 주세요"
              autoComplete="current-password"
              {...register("password")}
              error={errors.password?.message}
              rightIcon={
                <button
                  type="button"
                  className="text-xs text-[#888] hover:text-[#555]"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? "숨기기" : "보기"}
                </button>
              }
              style={{ width: "100%", height: "48px", borderRadius: "8px" }}
            />
            <div className="flex justify-end mt-2">
              <Link to="/auth/forgot-password" className="text-sm text-[#4285F4] hover:underline">
                비밀번호를 잊으셨나요?
              </Link>
            </div>
          </div>
          <Button
            type="submit"
            loading={submitting}
            className="w-full bg-[#222] hover:bg-[#333] text-white font-semibold"
            style={{ height: "48px", borderRadius: "8px" }}
          >
            로그인하고 초대 수락
          </Button>
        </form>
      )}

      {socials.length > 0 && (
        <div className={hasEmailLogin ? "mt-6 pt-5 border-t border-[#eee]" : ""}>
          <p className="text-[13px] text-[#888] text-center mb-3">
            {hasEmailLogin ? "또는 가입한 소셜 계정으로 로그인" : "가입한 소셜 계정으로 로그인하면 초대가 수락됩니다."}
          </p>
          <div className="flex justify-center gap-6">
            {socials.map((s) => (
              <button
                key={s.method}
                type="button"
                disabled={submitting}
                onClick={() => startSocial(s.provider)}
                className="transition-transform hover:scale-105 disabled:opacity-50"
                aria-label={s.label}
              >
                <img src={s.icon} alt={s.label} className="w-[56px] h-[56px]" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const SignupSection: React.FC<{
  email: string;
  submitting: boolean;
  onSubmit: (form: SignupForm) => void;
}> = ({ email, submitting, onSubmit }) => {
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<SignupForm>({
    resolver: zodResolver(SignupFormSchema),
    defaultValues: {
      name: "",
      password: "",
      confirmPassword: "",
      agreementTerms: false,
      agreementPrivacy: false,
      agreementAge: false,
      agreementMarketing: false,
    },
  });

  const agreementKeys = ["agreementTerms", "agreementPrivacy", "agreementAge", "agreementMarketing"] as const;
  const allAgreed = agreementKeys.every((key) => watch(key));
  const toggleAll = () => agreementKeys.forEach((key) => setValue(key, !allAgreed, { shouldValidate: key !== "agreementMarketing" }));

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-[13px] text-[#555] bg-[#FAFAFA] rounded-[8px] px-3 py-2">
        이 이메일로 가입한 계정이 없습니다. 가입하면 바로 회사 관리자로 등록됩니다.
      </p>
      <div>
        <label className="block text-sm font-medium text-[#555] mb-1">이메일</label>
        <Input value={email} readOnly disabled style={{ width: "100%", height: "48px", borderRadius: "8px" }} />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#555] mb-1">이름</label>
        <Input
          placeholder="이름을 입력해 주세요"
          {...register("name")}
          error={errors.name?.message}
          style={{ width: "100%", height: "48px", borderRadius: "8px" }}
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#555] mb-1">비밀번호</label>
        <Input
          type={showPassword ? "text" : "password"}
          placeholder="비밀번호를 입력해 주세요"
          autoComplete="new-password"
          {...register("password")}
          error={errors.password?.message}
          rightIcon={
            <button
              type="button"
              className="text-xs text-[#888] hover:text-[#555]"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? "숨기기" : "보기"}
            </button>
          }
          style={{ width: "100%", height: "48px", borderRadius: "8px" }}
        />
        <p className="text-xs text-[#888] mt-1">8-16자, 영문+숫자+특수문자 포함</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-[#555] mb-1">비밀번호 확인</label>
        <Input
          type={showPassword ? "text" : "password"}
          placeholder="비밀번호를 다시 입력해 주세요"
          autoComplete="new-password"
          {...register("confirmPassword")}
          error={errors.confirmPassword?.message}
          style={{ width: "100%", height: "48px", borderRadius: "8px" }}
        />
      </div>

      <div className="rounded-[8px] border border-[#e0e0e0] p-4 space-y-2.5">
        <AgreementCheck checked={allAgreed} onChange={toggleAll} label="전체 동의" strong />
        <div className="border-t border-[#eee]" />
        <AgreementCheck
          checked={watch("agreementTerms")}
          onChange={() => setValue("agreementTerms", !watch("agreementTerms"), { shouldValidate: true })}
          label="(필수) 서비스 이용약관 동의"
          error={errors.agreementTerms?.message}
        />
        <AgreementCheck
          checked={watch("agreementPrivacy")}
          onChange={() => setValue("agreementPrivacy", !watch("agreementPrivacy"), { shouldValidate: true })}
          label="(필수) 개인정보 수집 및 이용 동의"
          error={errors.agreementPrivacy?.message}
        />
        <AgreementCheck
          checked={watch("agreementAge")}
          onChange={() => setValue("agreementAge", !watch("agreementAge"), { shouldValidate: true })}
          label="(필수) 만 14세 이상입니다"
          error={errors.agreementAge?.message}
        />
        <AgreementCheck
          checked={watch("agreementMarketing")}
          onChange={() => setValue("agreementMarketing", !watch("agreementMarketing"))}
          label="(선택) 마케팅 정보 수신 동의"
        />
      </div>

      <Button
        type="submit"
        loading={submitting}
        className="w-full bg-[#222] hover:bg-[#333] text-white font-semibold"
        style={{ height: "48px", borderRadius: "8px" }}
      >
        가입하고 초대 수락
      </Button>
    </form>
  );
};

const AgreementCheck: React.FC<{
  checked: boolean;
  onChange: () => void;
  label: string;
  strong?: boolean;
  error?: string;
}> = ({ checked, onChange, label, strong, error }) => (
  <div>
    <label className="flex items-center cursor-pointer">
      <input type="checkbox" checked={checked} onChange={onChange} className="sr-only" />
      <img
        src={checked ? "/assets/icons/checkbox-checked.svg" : "/assets/icons/checkbox-unchecked.svg"}
        alt=""
        className="w-5 h-5"
      />
      <span className={`ml-2 text-[14px] ${strong ? "font-semibold text-[#222]" : "text-[#555]"}`}>{label}</span>
    </label>
    {error && <p className="text-[12px] text-[#4285F4] ml-7 mt-0.5">{error}</p>}
  </div>
);

const StatusCard: React.FC<{ title: string; message: string; tone?: "blue" | "red" }> = ({
  title,
  message,
  tone = "red",
}) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
    <div className="max-w-md w-full bg-white rounded-[12px] shadow-lg p-8 text-center">
      <div
        className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${
          tone === "blue" ? "bg-[#E8EFFF]" : "bg-[#FEF2F2]"
        }`}
      >
        <svg
          className={`w-8 h-8 ${tone === "blue" ? "text-[#4285F4]" : "text-[#F24A4A]"}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {tone === "blue" ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          ) : (
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          )}
        </svg>
      </div>
      <h2 className="text-xl font-semibold text-[#222] mb-3">{title}</h2>
      <p className="text-[#555] mb-6">{message}</p>
      <Link to="/">
        <Button
          type="button"
          className="w-full bg-[#222] text-white hover:bg-[#333]"
          style={{ height: "48px", borderRadius: "8px" }}
        >
          처음으로
        </Button>
      </Link>
    </div>
  </div>
);

export default InvitationAcceptPage;
